import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import Isvg from 'react-inlinesvg';

import Button from 'components/Button';
import Message from 'components/Message';
import { PARTNER_DASHBOARD_UNLOCK_CLASS_LEVEL_CAMPAIGNS } from 'components/Optimizely/Optimizely.features';
import { TooltipContent, TooltipToggle } from 'components/Tooltip';
import When from 'components/When';
import { CONFIDENCE_ICONS, ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { isVenueDashboardSubtypeFoodAndBeverage } from 'helpers/ia-logic';
import { naiveDateStringToDateObject } from 'helpers/intl-fns';
import { selectActiveVenue } from 'selectors/general';

import { CAMPAIGN_LEVEL, CAMPAIGN_TYPE } from '../Campaigns.helpers';
import parentMessages from '../Campaigns.messages';

import './CampaignsV2ConfirmationModal.scss';

const messages = defineMessages({
  heading: {
    id: 'campaigns-v2-confirmation-modal.heading',
    defaultMessage: 'Confirm campaign',
  },
  targetAudience: {
    id: 'campaigns-v2-confirmation-modal.target-audience',
    defaultMessage: 'Target audience',
  },
  services: {
    id: 'campaigns-v2-confirmation-modal.services',
    defaultMessage: 'Services',
  },
  discountAmount: {
    id: 'campaigns-v2-confirmation-modal.discount-amount',
    defaultMessage: 'Discount amount',
  },
  startDate: {
    id: 'campaigns-v2-confirmation-modal.start-date',
    defaultMessage: 'Start date',
  },
  endDate: {
    id: 'campaigns-v2-confirmation-modal.end-date',
    defaultMessage: 'End date',
  },
  discountPercentage: {
    id: 'campaigns-v2-confirmation-modal.discount-percentage',
    defaultMessage: '{value}% discount',
  },
  disclaimerCreate: {
    id: 'campaigns-v2-confirmation-modal.disclaimer',
    defaultMessage:
      'By clicking “Launch campaign”, you agree that payments to you will be discounted by the applicable percentage shown for any reservations booked through this campaign.',
  },
  disclaimerEdit: {
    id: 'campaigns-v2-confirmation-modal.disclaimer-edit',
    defaultMessage:
      'By clicking “Confirm”, you agree that payments to you will be discounted by the applicable percentage shown for any reservations booked through this campaign.',
  },
  cancel: {
    id: 'campaigns-v2-confirmation-modal.cancel',
    defaultMessage: 'Cancel',
  },
  launchCampaign: {
    id: 'campaigns-v2-confirmation-modal.launch-campaign',
    defaultMessage: 'Launch campaign',
  },
  confirm: {
    id: 'campaigns-v2-confirmation-modal.confirm',
    defaultMessage: 'Confirm',
  },
  allServices: {
    id: 'campaigns-v2-confirmation-modal.all-services',
    defaultMessage: 'All services',
  },
  moreInformation: {
    id: 'campaigns-v2-confirmation-modal.more-information',
    defaultMessage: 'More information',
  },
  classes: {
    id: 'campaigns-v2-confirmation-modal.classes',
    defaultMessage: 'Classes',
  },
  allClasses: {
    id: 'campaigns-v2-confirmation-modal.all-classes',
    defaultMessage: 'All classes',
  },
  items: {
    id: 'campaigns-v2-confirmation-modal.items',
    defaultMessage: 'Items',
  },
  allItems: {
    id: 'campaigns-v2-confirmation-modal.all-items',
    defaultMessage: 'All items',
  },
});

const campaignsTooltipsMessages = defineMessages({
  [CAMPAIGN_TYPE.ALL_USER]: {
    id: 'campaigns-v2-confirmation-modal.tooltip-all-user',
    defaultMessage:
      'General promotion for all members. If more than one promotion applies a member will see the largest discount',
  },
  [CAMPAIGN_TYPE.NEW_USER]: {
    id: 'campaigns-v2-confirmation-modal.tooltip-new-user',
    defaultMessage:
      'Attract new members to your venue by providing a discount on their first reservation at your venue',
  },
  [CAMPAIGN_TYPE.WINBACK]: {
    id: 'campaigns-v2-confirmation-modal.tooltip-winback',
    defaultMessage:
      'Incentivize members to return to your venue by providing a discount to users who have not attended a class at your venue in at least 3 months',
  },
});

const { block, element } = bem('campaigns-v2-confirmation-modal');

function CampaignsV2ConfirmationModal({
  isAppointmentTypeVenue,
  startDate,
  endDate,
  classes,
  discountPercentage,
  onModalConfirm,
  onClose,
  setEndDate,
  partnerCampaignType,
  isCreate = false,
}) {
  const intl = useIntl();
  const venue = useSelector(selectActiveVenue);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isFoodAndBeverage = isVenueDashboardSubtypeFoodAndBeverage(venue);

  const nameOverrides = isFoodAndBeverage ? parentMessages.fnbNameOverrides : parentMessages.nameOverrides;
  const disclaimerMessage = isCreate ? messages.disclaimerCreate : messages.disclaimerEdit;

  const adjustedStartDate = isCreate ? startDate : naiveDateStringToDateObject(startDate);
  const adjustedEndDate = isCreate ? endDate : naiveDateStringToDateObject(endDate);

  const shouldShowSelectedClasses = isAppointmentTypeVenue || window[PARTNER_DASHBOARD_UNLOCK_CLASS_LEVEL_CAMPAIGNS];

  function onConfirm() {
    setIsSubmitting(true);
    onModalConfirm();
  }

  const { icon, heading, all } = (() => {
    if (isFoodAndBeverage) {
      return { icon: ICONS.concierge, heading: messages.items, all: messages.allItems };
    }

    if (isAppointmentTypeVenue) {
      return { icon: ICONS.concierge, heading: messages.services, all: messages.allServices };
    }

    return { icon: ICONS.dumbbell, heading: messages.classes, all: messages.allClasses };
  })();

  return (
    <div className={block()}>
      <Message className={element('heading')} as="h2" message={messages.heading} />

      <section className={element('info-section')}>
        <article className={element('info-row')}>
          <Isvg className={element('info-row-icon')} src={CONFIDENCE_ICONS.capacity} />
          <Message className={element('info-row-heading')} as="h3" message={messages.targetAudience} />

          <span className={element('info-row-text')}>
            <Message message={nameOverrides[partnerCampaignType.name]} />

            <TooltipToggle name="target-audience-tooltip" className={element('info-row-tooltip')}>
              <Isvg alt={intl.formatMessage(messages.moreInformation)} src={ICONS.info} />
            </TooltipToggle>
            <TooltipContent name="target-audience-tooltip" hoverable place="right">
              <Message
                className={element('info-row-tooltip-content')}
                message={campaignsTooltipsMessages[partnerCampaignType.name]}
              />
            </TooltipContent>
          </span>
        </article>

        <div className={element('info-divider')} />

        <When predicate={shouldShowSelectedClasses}>
          <article className={element('info-row')}>
            <Isvg className={element('info-row-icon')} src={icon} />
            <Message className={element('info-row-heading')} as="h3" message={heading} />

            <When predicate={partnerCampaignType.level === CAMPAIGN_LEVEL.VENUE}>
              <Message className={element('info-row-text')} message={all} />
            </When>

            <When predicate={partnerCampaignType.level !== CAMPAIGN_LEVEL.VENUE}>
              <section className={element('info-row-collection')}>
                {classes.map(({ name: className, classId }) => (
                  <div key={classId} className={element('pill')}>
                    {className}
                  </div>
                ))}
              </section>
            </When>
          </article>

          <div className={element('info-divider')} />
        </When>

        <article className={element('info-row')}>
          <Isvg src={ICONS.tagHollow} />
          <Message className={element('info-row-heading')} as="h3" message={messages.discountAmount} />
          <Message
            className={element('info-row-text')}
            message={messages.discountPercentage}
            values={{ value: discountPercentage }}
          />
        </article>

        <div className={element('info-divider')} />

        <article className={element('info-row')}>
          <Isvg src={ICONS.calendarDark} />
          <Message className={element('info-row-heading')} as="h3" message={messages.startDate} />
          <Message className={element('info-row-text')} value={new Date(adjustedStartDate)} format="medium" />
        </article>

        <When predicate={setEndDate && !!endDate}>
          <div className={element('info-divider')} />

          <article className={element('info-row')}>
            <Isvg src={ICONS.calendarDark} />
            <Message className={element('info-row-heading')} as="h3" message={messages.endDate} />
            <Message className={element('info-row-text')} value={new Date(adjustedEndDate)} format="medium" />
          </article>
        </When>
      </section>

      <section className={element('disclaimer')}>
        <Isvg className={element('disclaimer-icon')} src={ICONS.infoGreyLight} />
        <Message className={element('disclaimer-text')} message={disclaimerMessage} />
      </section>

      <section className={element('buttons')}>
        <Message
          className={element('button')}
          as={Button}
          styled="tertiary"
          message={messages.cancel}
          onClick={onClose}
        />

        <When predicate={isCreate}>
          <Message
            className={element('button')}
            as={Button}
            message={messages.launchCampaign}
            onClick={onConfirm}
            waiting={isSubmitting}
          />
        </When>

        <When predicate={!isCreate}>
          <Message
            className={element('button')}
            as={Button}
            message={messages.confirm}
            onClick={onConfirm}
            waiting={isSubmitting}
          />
        </When>
      </section>
    </div>
  );
}

CampaignsV2ConfirmationModal.propTypes = {
  isAppointmentTypeVenue: PropTypes.bool,
  startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  classes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })
  ),
  discountPercentage: PropTypes.number,
  onModalConfirm: PropTypes.func,
  onClose: PropTypes.func,
  setEndDate: PropTypes.bool,
  partnerCampaignType: PropTypes.shape({
    name: PropTypes.oneOf(Object.keys(CAMPAIGN_TYPE)),
    level: PropTypes.oneOf(Object.keys(CAMPAIGN_LEVEL)),
  }),
  isCreate: PropTypes.bool,
};

export default CampaignsV2ConfirmationModal;
